import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ClipboardCheckIcon, PlusCircleIcon } from 'lucide-react';
import { ManualSampleCapture } from './ManualSampleCapture';
interface Sample {
  id: number;
  title: string;
  timestamp: string;
  values: {
    id: string;
    label: string;
    value: string;
    type: string;
  }[];
  status: 'pending' | 'completed';
}
interface LogListProps {
  isCompleted: boolean;
}
const initialSamples = [{
  id: 1,
  title: 'QS Probe',
  timestamp: '10:30',
  values: [{
    id: 'field1',
    label: 'Haben die Güsse flecken?',
    value: 'true',
    type: 'boolean'
  }, {
    id: 'field2',
    label: 'Wurde das Rostpaket in der Hammermühle gewechselt?',
    value: 'false',
    type: 'boolean'
  }],
  status: 'completed'
}, {
  id: 2,
  title: 'Test 1',
  timestamp: '11:30',
  values: [{
    id: 'field1',
    label: 'Passt das Granulat?',
    value: 'Ja, Granulat entspricht den Vorgaben',
    type: 'text'
  }, {
    id: 'field2',
    label: 'Entnehme 1 kg Feststoff',
    value: '1.2',
    type: 'number'
  }],
  status: 'completed'
}, {
  id: 3,
  title: 'Qualitätskontrolle',
  timestamp: '12:30',
  values: [{
    id: 'field1',
    label: 'pH-Wert',
    value: '7.2',
    type: 'number'
  }, {
    id: 'field2',
    label: 'Viskosität',
    value: '95.5',
    type: 'number'
  }, {
    id: 'field3',
    label: 'Bemerkungen zur Konsistenz',
    value: 'Normale Konsistenz, keine Auffälligkeiten',
    type: 'text'
  }],
  status: 'completed'
}];
export function LogList({
  isCompleted
}: LogListProps) {
  const [showManualCapture, setShowManualCapture] = useState(false);
  const [expandedSample, setExpandedSample] = useState<number | null>(null);
  const [samples, setSamples] = useState(initialSamples);
  const handleValueChange = (sampleId: number, fieldId: string, newValue: string) => {
    setSamples(samples.map(sample => {
      if (sample.id === sampleId) {
        return {
          ...sample,
          values: sample.values.map(value => value.id === fieldId ? {
            ...value,
            value: newValue
          } : value)
        };
      }
      return sample;
    }));
  };
  const formatValue = (value: string, type: string) => {
    switch (type) {
      case 'boolean':
        return value === 'true' ? 'Ja' : 'Nein';
      case 'number':
        return Number(value).toFixed(1);
      default:
        return value;
    }
  };
  const handleAddNewSample = (probeValues: Record<number, Record<string, string>>) => {
    const newId = Math.max(...samples.map(sample => sample.id), 0) + 1;
    const now = new Date();
    const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    let values = [];
    switch (Object.keys(probeValues)[0]) {
      case '1':
        // QS Probe
        values = [{
          id: 'field1',
          label: 'Haben die Güsse flecken?',
          value: probeValues[1]['field1'] || 'false',
          type: 'boolean'
        }, {
          id: 'field2',
          label: 'Wurde das Rostpaket in der Hammermühle gewechselt?',
          value: probeValues[1]['field2'] || 'false',
          type: 'boolean'
        }];
        break;
      case '2':
        // Test 1
        values = [{
          id: 'field1',
          label: 'Passt das Granulat?',
          value: probeValues[2]['field1'] || '',
          type: 'text'
        }, {
          id: 'field2',
          label: 'Entnehme 1 kg Feststoff',
          value: probeValues[2]['field2'] || '',
          type: 'number'
        }];
        break;
      case '3':
        // Qualitätskontrolle
        values = [{
          id: 'field1',
          label: 'pH-Wert',
          value: probeValues[3]['field1'] || '',
          type: 'number'
        }, {
          id: 'field2',
          label: 'Viskosität',
          value: probeValues[3]['field2'] || '',
          type: 'number'
        }, {
          id: 'field3',
          label: 'Bemerkungen zur Konsistenz',
          value: probeValues[3]['field3'] || '',
          type: 'text'
        }];
        break;
    }
    const newSample = {
      id: newId,
      title: values.length === 2 && values[0].type === 'boolean' ? 'QS Probe' : values.length === 2 ? 'Test 1' : 'Qualitätskontrolle',
      timestamp,
      values,
      status: 'pending'
    };
    setSamples([newSample, ...samples]);
    setExpandedSample(newId);
  };
  return <div className="space-y-4" data-id="element-550">
      <div className="flex justify-between items-center" data-id="element-551">
        <div className="flex items-center gap-2" data-id="element-552">
          <ClipboardCheckIcon className="text-[#3498dd]" size={20} data-id="element-553" />
          <h3 className="text-lg font-semibold text-gray-800" data-id="element-554">Historie</h3>
        </div>
        {!isCompleted && <button onClick={() => setShowManualCapture(true)} className="flex items-center gap-2 bg-[#3498dd] text-white px-3 py-1.5 rounded text-sm hover:bg-blue-600" data-id="element-555">
            <PlusCircleIcon size={16} data-id="element-556" />
            Neue Probe
          </button>}
      </div>
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-557">
        {samples.map(sample => <div key={sample.id} className="p-4" data-id="element-558">
            <div className="flex items-center justify-between" data-id="element-559">
              <div className="flex items-center gap-4" data-id="element-560">
                <span className="font-medium text-gray-700" data-id="element-561">
                  {sample.title}
                </span>
                <span className="text-sm text-gray-500" data-id="element-562">
                  {sample.timestamp}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${sample.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`} data-id="element-563">
                  {sample.status === 'completed' ? 'Abgeschlossen' : 'Ausstehend'}
                </span>
              </div>
              <button onClick={() => setExpandedSample(expandedSample === sample.id ? null : sample.id)} className="text-gray-400 hover:text-gray-600" data-id="element-564">
                {expandedSample === sample.id ? <ChevronUpIcon size={20} data-id="element-565" /> : <ChevronDownIcon size={20} data-id="element-566" />}
              </button>
            </div>
            {expandedSample === sample.id && <div className="mt-4 grid grid-cols-1 gap-4" data-id="element-567">
                {sample.values.map(field => <div key={field.id} className="space-y-1" data-id="element-568">
                    <label className="block text-xs text-gray-500" data-id="element-569">
                      {field.label}
                    </label>
                    {field.type === 'boolean' ? <div className="flex gap-4" data-id="element-570">
                        <label className="inline-flex items-center" data-id="element-571">
                          <input type="radio" value="true" checked={field.value === 'true'} onChange={e => handleValueChange(sample.id, field.id, e.target.value)} className="form-radio text-[#3498dd] focus:ring-[#3498dd]" data-id="element-572" />
                          <span className="ml-2 text-sm" data-id="element-573">Ja</span>
                        </label>
                        <label className="inline-flex items-center" data-id="element-574">
                          <input type="radio" value="false" checked={field.value === 'false'} onChange={e => handleValueChange(sample.id, field.id, e.target.value)} className="form-radio text-[#3498dd] focus:ring-[#3498dd]" data-id="element-575" />
                          <span className="ml-2 text-sm" data-id="element-576">Nein</span>
                        </label>
                      </div> : <input type={field.type === 'number' ? 'number' : 'text'} value={field.value} onChange={e => handleValueChange(sample.id, field.id, e.target.value)} className="w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#3498dd]" data-id="element-577" />}
                  </div>)}
              </div>}
          </div>)}
      </div>
      {showManualCapture && createPortal(<ManualSampleCapture onClose={() => setShowManualCapture(false)} onSave={handleAddNewSample} data-id="element-578" />, document.body)}
    </div>;
}